import React, { useEffect, useRef, useState } from "react";

interface LazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  prefetchUrls?: string[]; // URLs to preload when this image becomes visible
  rootMargin?: string; // IntersectionObserver root margin
  isDarkMode?: boolean; // For checkerboard pattern styling
  showPlaceholder?: boolean; // Whether to show checkerboard placeholder
  placeholderEffect?: 'pulse' | 'shimmer'; // Loading effect style
  forceLoad?: boolean;
}

/**
 * LazyImage
 * - Defers setting the real src until the element is near/in the viewport
 * - Uses IntersectionObserver with a generous rootMargin for fast perceived load
 * - Optionally prefetches a few upcoming images once visible
 */
export const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  className,
  onError,
  onLoad,
  prefetchUrls,
  rootMargin = "300px",
  isDarkMode = false,
  showPlaceholder = true,
  placeholderEffect = 'shimmer',
  forceLoad = false,
  ...imgProps
}) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(forceLoad);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (forceLoad) {
      setInView(true);
      return;
    }
    const el = containerRef.current;
    if (!el) return;

    // If browser already supports native lazy loading, still keep IO for prefetch-ahead
    let observer: IntersectionObserver | null = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting || entry.intersectionRatio > 0) {
            setInView(true);
          }
        });
      },
      {
        root: null,
        rootMargin,
        threshold: [0, 0.01, 0.1],
      }
    );

    observer.observe(el);
    return () => {
      if (observer && el) observer.unobserve(el);
      observer = null;
    };
  }, [rootMargin, forceLoad]);

  // Prefetch next URLs when this one becomes visible (once)
  useEffect(() => {
    if (!inView || !prefetchUrls || prefetchUrls.length === 0) return;
    prefetchUrls.forEach((u) => {
      if (!u) return;
      const img = new Image();
      img.decoding = "async";
      img.loading = "lazy" as any;
      img.src = u;
    });
  }, [inView, prefetchUrls]);

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setLoaded(true);
    setError(false);
    onLoad?.(e);
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setError(true);
    onError?.(e);
  };

  // Checkerboard background for transparency preview
  const getCheckerboardStyle = (): React.CSSProperties => {
    const lightBase = '#ffffff';
    const lightSquare = '#e5e7eb'; // gray-200
    const darkBase = '#1a1a1a';
    const darkSquare = '#2a2a2a'; // darker gray for contrast

    const base = isDarkMode ? darkBase : lightBase;
    const square = isDarkMode ? darkSquare : lightSquare;

    return {
      backgroundColor: base,
      backgroundImage: `
        linear-gradient(45deg, ${square} 25%, transparent 25%, transparent 75%, ${square} 75%, ${square}),
        linear-gradient(45deg, ${square} 25%, transparent 25%, transparent 75%, ${square} 75%, ${square})
      `,
      backgroundPosition: '0 0, 8px 8px',
      backgroundSize: '16px 16px',
    };
  };

  // Shimmer overlay style (simulated using gradient + transform animation via Tailwind pulse)
  const getOverlayStyle = (): React.CSSProperties => {
    const from = isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
    const to = isDarkMode ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)';
    return {
      backgroundImage: `linear-gradient(90deg, transparent 0%, ${from} 30%, ${to} 50%, ${from} 70%, transparent 100%)`,
      backgroundSize: '200% 100%',
      opacity: inView ? 0.6 : 0.9,
      transition: 'opacity 0.3s ease-in-out',
    };
  };

  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;

    const priority = inView && !loaded ? "high" : "auto";
    el.setAttribute("fetchpriority", priority);

    return () => {
      el.removeAttribute("fetchpriority");
    };
  }, [inView, loaded]);

  return (
    <div
      ref={containerRef}
      className="relative block overflow-hidden rounded-none"
      style={{
        width: imgProps.width || 'auto',
        height: imgProps.height || 'auto',
      }}
    >
      {/* Checkerboard placeholder */}
      {showPlaceholder && !loaded && !error && (
        <div
          className="absolute inset-0 rounded-lg"
          style={{
            ...getCheckerboardStyle(),
            opacity: inView ? 0.5 : 1,
            transition: 'opacity 0.3s ease-in-out',
          }}
        />
      )}
      
      {/* Animated shimmer/pulse overlay while loading */}
      {showPlaceholder && !loaded && !error && (
        <div
          className={`absolute inset-0 pointer-events-none ${placeholderEffect === 'pulse' ? 'animate-pulse' : ''}`}
          style={{
            ...getOverlayStyle(),
            backgroundRepeat: 'no-repeat',
            backgroundPosition: '0% 0%',
          }}
        />
      )}
      
      {/* Actual image */}
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <img
        ref={imgRef}
        alt={alt}
        className={`block ${className || ''}`}
        // Only set the src when inView to prevent offscreen downloads
        src={inView ? src : undefined}
        // Keep native hints too
        loading={forceLoad ? "eager" : "lazy"}
        decoding="async"
        onLoad={handleLoad}
        onError={handleError}
        style={{
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
          ...imgProps.style,
        }}
        {...imgProps}
      />
    </div>
  );
};

export default LazyImage;
